import React, { useEffect, useMemo, useState } from 'react';
import { actionType, initialEmployeeState, toastMessages } from 'config/constants';
import { AgendaApi } from 'services';
import { BurgerMenu } from 'components/BurgerMenu';
import { checkFileTotalSize, createAgendaBody } from 'utils';
import { CircularProgress, Grid } from '@material-ui/core';
import { HeaderParameters } from '../components';
import { isNil } from 'lodash';
import { useEmployee, useEmployeeFunctions } from 'contexts/EmployeeContext';
import { useCompany } from 'contexts/CompanyContext';
import { useHistory } from 'react-router-dom';
import ActionOnEmployeeModal from 'components/ActionOnEmployee';
import AgendaTransferModal from 'components/AgendaTransferModal';
import BlockWithoutStepModal, { ENDING_LOCATIONS } from 'components/BlockWithoutStepModal';
import CustomSelect from 'components/CustomSelect';
import EmployeeForm from 'components/EmployeeForm';
import routes from 'config/routes';
import team from '_assets/register/team.svg';
import toast from 'react-hot-toast';
import useShowActionModal from 'hooks/useShowActionModal';
import useStyles from './styles';

function EmployeesParameters() {
  const classes = useStyles();

  const { employees } = useEmployee();
  const { createEmployee, deleteEmployee, editEmployee, editEmployeePicture } = useEmployeeFunctions();
  const { companySteps, companyStepLoading } = useCompany();
  const history = useHistory();

  const firstEmployee = useMemo(() => employees[0] ?? null, [employees]);

  const [monitor, setMonitor] = useState(firstEmployee ?? initialEmployeeState);
  const [activities, setActivities] = useState([]);
  const [selectedMonitor, setSelectedMonitor] = useState(monitor);
  const [isNewMonitor, setIsNewMonitor] = useState(false);
  const [formDisabled, setFormDisabled] = useState(true);

  const [eventsTransfer, setEventsTransfer] = useState(null);
  const [openAgendaTransfer, setOpenAgendaTransfer] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isPageBlocked, setIsPageBlocked] = useState(false);

  const {
    previousChoice,
    resetShowAction,
    setActionConflictSelect,
    setActionSelect,
    setActionTransfer,
    shouldShowAction,
    showAction,
  } = useShowActionModal(actionType.DELETE, 'employee', removeEmployee, activities);

  useEffect(() => {
    setIsPageBlocked(!companySteps.employee_created);
  }, [companySteps]);

  useEffect(() => {
    setActivities([]);
    if (!isNil(monitor?.id)) {
      // Check if the selected employee has any activities associated
      AgendaApi.getAgendaByEmployee(monitor.id).then(({ activities }) => {
        setActivities(activities);
      });
    }
  }, [monitor?.id]);

  // Update form when deleted
  useEffect(() => {
    if (monitor?.id) {
      if (!employees.some((e) => e.id === monitor.id)) {
        setMonitor(firstEmployee ?? initialEmployeeState);
      }
    }
  }, [employees, firstEmployee, monitor]);

  const onPictureChange = async (picture) => {
    let picUpdated = false;
    if (picture?.file) {
      if (!checkFileTotalSize([picture.file])) {
        toast.error(toastMessages.error.UPLOAD_PICTURE_SIZE_LIMIT);
        return false;
      }

      let formData = new FormData();
      formData.append('file', picture?.file);
      picUpdated = await editEmployeePicture(monitor?.id, formData, picture?.url);
    }

    return picUpdated;
  };

  const handleTransfer = async (events) => {
    const agendas = createAgendaBody(events);
    try {
      await AgendaApi.transferAgenda({ agendas });
      toast.success(toastMessages.success.TRANSFER_AGENDA);
      return true;
    } catch (_) {
      toast.error(toastMessages.error.TRANSFER_AGENDA);
      return false;
    }
  };

  const handleCreateEmployee = async (values, picture) => {
    let pictureData = null;
    if (picture?.file) {
      if (!checkFileTotalSize([picture.file])) {
        toast.error(toastMessages.error.UPDATE_EMPLOYEE_PROFILE_PICTURE_SIZE_LIMIT);
        return { pictureSizeError: true };
      }

      let formData = new FormData();
      formData.append('file', picture?.file);
      pictureData = { data: formData, url: picture?.url };
    }

    const { created, pictureUpdated, item } = await createEmployee(values, pictureData);
    if (created) {
      setFormDisabled(true);
      setIsNewMonitor(false);
      setMonitor(item);
    }

    return { pictureUpdated, success: created };
  };

  const handleEditEmployee = async (values) => {
    if (eventsTransfer) await handleTransfer(eventsTransfer);

    const { edited, item } = await editEmployee(monitor, values);
    if (edited) {
      setFormDisabled(true);
      setIsNewMonitor(false);
      setMonitor(item);
      setEventsTransfer(null);
    }

    return { success: edited };
  };

  const handleStatusChanged = async ({ action, force, is_enabled, transfer }) => {
    let body = { is_enabled, id: monitor.id };

    if (!is_enabled) {
      if (['DEACTIVATE', 'DELETE'].includes(action)) body = { ...body, action, force };
      else if (transfer) {
        return handleOpenAgendaTransfer(actionType.DEACTIVATE);
      }
    }

    const { conflict, edited, item } = await editEmployee(monitor, body);
    if (edited) setMonitor(item);

    return { conflict, success: edited };
  };

  async function removeEmployee(data) {
    let body = {};
    if (['DEACTIVATE', 'DELETE'].includes(data.action)) body = { ...data };

    const { conflict, deleted } = await deleteEmployee(monitor.id, body);
    if (deleted) setMonitor(firstEmployee ?? initialEmployeeState);

    return { conflict, success: deleted };
  }

  const onEmployeeClick = (employee) => {
    if (isNewMonitor || !formDisabled) return;

    setFormDisabled(true);
    setSelectedMonitor(employee);
    setMonitor(employee);
  };

  const onAddNewEmployeeClick = () => {
    if (isNewMonitor || !formDisabled) return;

    const newMonitor = { ...initialEmployeeState, is_new: true };
    setMonitor(newMonitor);
    setIsNewMonitor(true);
    setFormDisabled(false);
  };

  const handleDeleteEmployee = async (action, force = false) => {
    setActionLoading(true);
    const response = await removeEmployee({ action, force });
    setActionLoading(false);
    return response;
  };

  const handleConfirmAction = async ({ forceAction, selectedAction, transfer }) => {
    // Handle action selection submit
    if (selectedAction) {
      // Selected action is DEACTIVATE or DELETE
      const { conflict } = await handleDeleteEmployee(selectedAction);
      if (conflict) return setActionConflictSelect(selectedAction);
      return resetShowAction();
    }

    // Handle confirmation on delete or deactivation
    if (!isNil(forceAction)) {
      if (forceAction) {
        await handleDeleteEmployee(previousChoice, true);
        return resetShowAction();
      }

      return setActionSelect();
    }

    // Handle transfer action
    if (transfer) {
      if (transfer === 'TRANSFER') {
        await handleOpenAgendaTransfer(actionType.DELETE);
        return resetShowAction();
      }

      return setActionSelect();
    }

    // Handle preference passed
    if (activities.length > 0) return setActionTransfer();
    setActionLoading(true);
    await removeEmployee({});
    setActionLoading(false);
    resetShowAction();
  };

  const handleCancelTransfer = () => {
    setCurrentAction(null);
    setOpenAgendaTransfer(false);
    // Trigger form reset
    setMonitor(initialEmployeeState);
    setTimeout(() => setMonitor(selectedMonitor));
  };

  const handleConfirmTransfer = async (events) => {
    setActionLoading(true);

    // Handle transfer
    const transferred = await handleTransfer(events);
    if (!transferred) return setActionLoading(false);

    // If transfer passed, handle specific action
    if (currentAction === actionType.DEACTIVATE) {
      const { edited, item } = await editEmployee(monitor, { id: monitor.id, is_enabled: false });
      if (edited) {
        setMonitor(item);
        setOpenAgendaTransfer(false);
      }
      return setActionLoading(false);
    }

    const { deleted } = await deleteEmployee(monitor.id, {});
    if (deleted) setOpenAgendaTransfer(false);
    setOpenAgendaTransfer(false);
    setActionLoading(false);
  };

  const handleOpenAgendaTransfer = (action) => {
    setCurrentAction(action);
    setOpenAgendaTransfer(true);
  };

  const handleCancelForm = () => {
    if (isNewMonitor) {
      setMonitor(firstEmployee ?? initialEmployeeState);
      setIsNewMonitor(false);
    } else if (!formDisabled) {
      setMonitor(selectedMonitor);
    }

    setFormDisabled(true);
    setEventsTransfer(null);
  };

  const handleSubmitForm = async ({ values, picture }) => {
    if (isNewMonitor) return await handleCreateEmployee(values, picture);
    else return await handleEditEmployee(values);
  };

  const handleBlockedPageClose = () => {
    history.push(routes.home.root);
  };

  return !!companyStepLoading ? (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress size={100} />
    </div>
  ) : (
    <>
      <Grid className={classes.root} container direction="column" justifyContent="center">
        <Grid className={classes.title} container item xs={12} justifyContent="center" style={{ flexGrow: 0 }}>
          <HeaderParameters image={team} noMargin text="Équipe" />
        </Grid>

        <Grid container item justifyContent="center" style={{ flexGrow: 0 }}>
          <Grid container item sm={12} md={9} alignItems="center" justifyContent="space-between">
            <Grid container item xs={11} justifyContent="center">
              <CustomSelect
                itemList={employees.map((m) => ({ displayName: `${m.first_name} ${m.last_name}`, ...m }))}
                labelField="displayName"
                idField="id"
                selectedItem={monitor}
                onClick={onEmployeeClick}
              />
            </Grid>

            <Grid container item xs={1} justifyContent="flex-end">
              <BurgerMenu>
                <BurgerMenu.Item disabled={isNewMonitor} onClick={onAddNewEmployeeClick}>
                  Ajouter
                </BurgerMenu.Item>
                <BurgerMenu.Item
                  disabled={isNewMonitor || employees.length === 0}
                  onClick={() => setFormDisabled(false)}
                >
                  Modifier
                </BurgerMenu.Item>
                <BurgerMenu.Item disabled={isNewMonitor || employees.length === 0} onClick={shouldShowAction}>
                  Supprimer
                </BurgerMenu.Item>
              </BurgerMenu>
            </Grid>
          </Grid>
        </Grid>

        <Grid container item justifyContent="center" style={{ flexGrow: 1 }}>
          <EmployeeForm
            id="employeeForm1"
            activities={activities}
            autoSavePicture={!isNewMonitor && firstEmployee}
            disabled={formDisabled}
            employee={monitor}
            onCancel={handleCancelForm}
            onPictureChange={onPictureChange}
            onStatusChange={handleStatusChanged}
            onSubmit={handleSubmitForm}
          />
        </Grid>

        <ActionOnEmployeeModal
          loading={actionLoading}
          modalType={actionType.DELETE}
          onClose={resetShowAction}
          onConfirm={handleConfirmAction}
          showAction={showAction}
        />

        <AgendaTransferModal
          employee={monitor}
          loading={actionLoading}
          open={openAgendaTransfer}
          title="Transfert Agenda"
          onClose={handleCancelTransfer}
          onConfirm={handleConfirmTransfer}
        />
      </Grid>
      <BlockWithoutStepModal
        endingLocation={ENDING_LOCATIONS.employee}
        open={isPageBlocked}
        onClose={handleBlockedPageClose}
      />
    </>
  );
}

export default EmployeesParameters;
